import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { Skeleton } from "@/components/ui/skeleton";
import { TrendingUp, DollarSign, BarChart4, Eye } from 'lucide-react';

interface InvestorMetrics {
  total_investments: number;
  portfolio_value: number;
  avg_return_rate: number;
  dividends_received: number;
  viewed_profiles: number;
  active_deals: number;
}

export const InvestorDashboardMetrics = () => {
  const { user } = useAuth();
  const [metrics, setMetrics] = useState<InvestorMetrics | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (user) {
      fetchInvestorMetrics(user.id);
    }
  }, [user]);

  const fetchInvestorMetrics = async (userId: string) => {
    try {
      setLoading(true);
      const { data, error } = await supabase
        .from('investor_metrics')
        .select('*')
        .eq('user_id', userId)
        .single();

      if (error) {
        console.error('Error fetching investor metrics:', error);
        return;
      }

      setMetrics(data);
    } catch (error) {
      console.error('Error fetching metrics:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      maximumFractionDigits: 0
    }).format(amount);
  };

  const formatPercentage = (value: number) => {
    return `${value.toFixed(2)}%`;
  };

  if (loading) {
    return (
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
        {[1, 2, 3, 4].map((i) => (
          <Card key={i} className="bg-card border border-border">
            <CardHeader className="pb-2">
              <div className="w-full h-4 bg-secondary/40 rounded animate-pulse mb-2"></div>
              <div className="w-2/3 h-3 bg-secondary/40 rounded animate-pulse"></div>
            </CardHeader>
            <CardContent>
              <div className="w-full h-8 bg-secondary/30 rounded animate-pulse"></div>
            </CardContent>
          </Card>
        ))}
      </div>
    );
  }

  if (!metrics) {
    return null;
  }

  const metricCards = [
    {
      title: "Portfolio Value",
      value: formatCurrency(metrics.portfolio_value),
      description: "Total value of investments",
      icon: <DollarSign className="h-5 w-5 text-green-500" />,
      change: "+0% from last month",
      color: "bg-green-50 dark:bg-green-900/20"
    },
    {
      title: "Investments",
      value: metrics.total_investments.toString(),
      description: "Number of active investments",
      icon: <BarChart4 className="h-5 w-5 text-blue-500" />,
      change: "",
      color: "bg-blue-50 dark:bg-blue-900/20"
    },
    {
      title: "Average Return",
      value: formatPercentage(metrics.avg_return_rate),
      description: "Across all investments",
      icon: <TrendingUp className="h-5 w-5 text-purple-500" />,
      change: "",
      color: "bg-purple-50 dark:bg-purple-900/20"
    },
    {
      title: "Viewed Businesses",
      value: metrics.viewed_profiles.toString(),
      description: "Businesses you've explored",
      icon: <Eye className="h-5 w-5 text-orange-500" />,
      change: "",
      color: "bg-orange-50 dark:bg-orange-900/20"
    }
  ];

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
      {metricCards.map((card, index) => (
        <Card key={index} className="bg-card border border-border">
          <CardHeader className="pb-2">
            <div className="flex justify-between items-start">
              <CardTitle className="text-sm font-medium text-muted-foreground">
                {card.title}
              </CardTitle>
              <div className={`p-2 rounded-full ${card.color}`}>
                {card.icon}
              </div>
            </div>
            <CardDescription>{card.description}</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{card.value}</div>
            {card.change && (
              <p className="text-xs text-muted-foreground mt-1">{card.change}</p>
            )}
          </CardContent>
        </Card>
      ))}
    </div>
  );
};
